/* global google */
import { GoogleMap, OverlayViewF, useJsApiLoader } from '@react-google-maps/api';
import {
    memo, ReactElement, ReactNode, useCallback, useEffect, useRef,
} from 'react';
import { GOOGLE_MAPS_KEY } from '../../const/environments.constants';
import { GOOGLE_LIBRARIES } from './googleLoader';
import { googleMapsStyles } from './googleMapStyles';
import MapLoader from './MapLoader';
import { LatLng } from './types';

interface GoogleMapViewProps {
    apiKey: string;
    center: LatLng;
    zoom?: number;
    bounds?: [number, number, number, number] | null;
    children?: ReactNode | ReactNode[];
    onLoaded: (map: google.maps.Map) => void;
    onCenterChanged?: (center: LatLng) => void;
    onLoadError: () => void;
}

interface ChildProps {
    lat: number;
    lng: number;
}

const isValidChildWithCoords = (child: unknown): child is ReactElement<ChildProps> => {
    if (!child || typeof child !== 'object') return false;

    const { props } = (child as { props?: unknown });

    if (!props || typeof props !== 'object') return false;

    const { lat, lng } = props as { lat?: unknown; lng?: unknown };

    return typeof lat === 'number' && typeof lng === 'number';
};

const renderOverlayChildren = (children: ReactNode | ReactNode[]) => {
    const nodes = Array.isArray(children) ? children : [children];

    return nodes
        .filter(isValidChildWithCoords)
        .map((child, index) => (
            <OverlayViewF
                key={child.key ?? index}
                position={{ lat: child.props.lat, lng: child.props.lng }}
                mapPaneName="overlayMouseTarget"
            >
                {child}
            </OverlayViewF>
        ));
};

const GoogleMapView = ({
    apiKey,
    center,
    zoom = 12,
    bounds,
    children,
    onLoaded,
    onCenterChanged,
    onLoadError,
}: GoogleMapViewProps) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey || GOOGLE_MAPS_KEY,
        libraries: GOOGLE_LIBRARIES,
    });

    const mapRef = useRef<google.maps.Map | null>(null);

    useEffect(() => {
        if (loadError) onLoadError();
    }, [loadError, onLoadError]);

    const fitToBoundsOrCenter = useCallback((map: google.maps.Map) => {
        if (bounds) {
            const gBounds = new google.maps.LatLngBounds(
                { lat: bounds[1], lng: bounds[0] },
                { lat: bounds[3], lng: bounds[2] },
            );

            map.fitBounds(gBounds, 80);

            return;
        }

        map.setCenter(center);
        map.setZoom(zoom);
    }, [bounds, center, zoom]);

    useEffect(() => {
        if (!mapRef.current) return;
        fitToBoundsOrCenter(mapRef.current);
    }, [fitToBoundsOrCenter]);

    const handleLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;

        if (onCenterChanged) {
            map.addListener('dragend', () => {
                const c = map.getCenter ? map.getCenter() : undefined;

                if (!c) return;
                onCenterChanged({ lat: c.lat(), lng: c.lng() });
            });
        }

        fitToBoundsOrCenter(map);
        onLoaded(map);
    }, [fitToBoundsOrCenter, onCenterChanged, onLoaded]);

    if (loadError) return null;

    if (!isLoaded) return <MapLoader />;

    return (
        <GoogleMap
            center={center}
            zoom={zoom}
            options={{ fullscreenControl: false, streetViewControl: false, styles: googleMapsStyles }}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            onLoad={handleLoad}
        >
            {renderOverlayChildren(children)}
        </GoogleMap>
    );
};

export default memo(GoogleMapView);
